import { employeeCurrent } from '@/lib/employee/employeeFunctions';
import store from '@/store';
import { Pounds } from '@/types/currencyTypes';
import { convertPenceToPounds } from '@/util/currencyFunctions';
import { isPast } from '@/util/dateArithmetic';
import spacetime from 'spacetime';
import {
  BillingOverview,
  BillingPackage,
  BillingPackageIntervalEnum,
  BillingPaymentMethod,
  BillingProductFeaturesEnum,
  BillingProductPrice,
  BillingProductTypeEnum,
  BillingQuotaDefinitionLookupKeyEnum,
  BillingSubscription,
  Employee,
} from '../../../api/v1';
import {
  PlanChangeIntervalEnum,
  PlanChangeLevelEnum,
  PlanNameEnum,
} from '../enum/billing/BillingEnum';

export type PlanChangeLevel =
  typeof PlanChangeLevelEnum[keyof typeof PlanChangeLevelEnum];
export type PlanChangeInterval =
  typeof PlanChangeIntervalEnum[keyof typeof PlanChangeIntervalEnum];

export const countEmployees = (employees: Employee[]) =>
  employees.filter(employeeCurrent).length;

export const getSubscriptionQuota = (
  billingSubscription: BillingSubscription,
  quota: BillingQuotaDefinitionLookupKeyEnum,
): number => {
  const quotaDefinition =
    store.getters['billing/billingQuotaDefinition'](quota);

  const quotaOverride = billingSubscription.billingQuotaOverrides.find(
    (o) => o.billingQuotaDefinitionId === quotaDefinition.id,
  );

  return quotaOverride?.value || quotaDefinition.defaultValue;
};

export const subscriptionQuotaExceeded = (
  billingSubscription: BillingSubscription,
  quota: BillingQuotaDefinitionLookupKeyEnum,
  count: number,
): boolean => count >= getSubscriptionQuota(billingSubscription, quota);

export const getPackageQuota = (
  billingPackage: BillingPackage,
  quota: BillingQuotaDefinitionLookupKeyEnum,
) => {
  const quotaDefinition =
    store.getters['billing/billingQuotaDefinition'](quota);

  const quotaOverride = billingPackage.billingPackageQuotaOverrides.find(
    (o) => o.billingQuotaDefinitionId === quotaDefinition.id,
  );

  return quotaOverride?.value || quotaDefinition.defaultValue;
};

export const packageQuotaExceeded = (
  billingPackage: BillingPackage,
  quota: BillingQuotaDefinitionLookupKeyEnum,
  count: number,
) => count > getPackageQuota(billingPackage, quota);

export const packageHasFeature = (
  billingPackage: BillingPackage,
  feature: BillingProductFeaturesEnum,
) =>
  billingPackage.billingProductPrices
    .flatMap((bpp) => bpp.billingProduct.features)
    .includes(feature);

export const employeeSoftLimitReached = (
  employees: Employee[],
  billingSubscription: BillingSubscription,
): boolean =>
  getSubscriptionQuota(
    billingSubscription,
    BillingQuotaDefinitionLookupKeyEnum.EmployeeSoftLimit,
  ) > 0 &&
  subscriptionQuotaExceeded(
    billingSubscription,
    BillingQuotaDefinitionLookupKeyEnum.EmployeeSoftLimit,
    countEmployees(employees),
  );

export const isTrialVersion = (
  billingSubscription: BillingSubscription,
  timezone: string,
) => {
  if (!billingSubscription.trialEndsOn) {
    return false;
  }

  const now = spacetime.now(timezone).startOf('day');
  const trialEnds = billingSubscription.trialEndsOn.toDate(timezone);

  return now.isBefore(trialEnds);
};

export const employeeSoftLimitNeedsPayment = (
  newEmployeesCount: number,
  billingSubscription: BillingSubscription,
  billingOverview: BillingOverview,
  timezone: string,
) => {
  const employeeSoftLimit = getSubscriptionQuota(
    billingSubscription,
    BillingQuotaDefinitionLookupKeyEnum.EmployeeSoftLimit,
  );
  return (
    employeeSoftLimit > 0 &&
    newEmployeesCount > employeeSoftLimit &&
    !billingOverview.hasPaymentMethods &&
    !isTrialVersion(billingSubscription, timezone)
  );
};

export const employeeHardLimitReached = (
  employees: Employee[],
  billingSubscription: BillingSubscription,
): boolean =>
  subscriptionQuotaExceeded(
    billingSubscription,
    BillingQuotaDefinitionLookupKeyEnum.EmployeeHardLimit,
    countEmployees(employees),
  );

export const allowedAdditionalEmployees = (
  employees: Employee[],
  billingSubscription: BillingSubscription,
): boolean => !employeeHardLimitReached(employees, billingSubscription);

export const isAdditionalEmployee = (
  employees: Employee[],
  billingSubscription: BillingSubscription,
): boolean => employeeSoftLimitReached(employees, billingSubscription);

export const getSubscriptionPriceByProductLookupKey = (
  billingSubscription: BillingSubscription,
  lookupKey: string,
): BillingProductPrice => {
  return billingSubscription.billingProductPrices.find(
    (bpp) => bpp.billingProduct.lookupKey === lookupKey,
  );
};

export const getPackagePriceByProductLookupKey = (
  billingPackage: BillingPackage,
  lookupKey: string,
) =>
  billingPackage.billingProductPrices.find(
    (bpp) => bpp.billingProduct.lookupKey === lookupKey,
  );

export const getPackagePricesByType = (
  billingPackage: BillingPackage,
  type: BillingProductTypeEnum,
): BillingProductPrice[] =>
  billingPackage.billingProductPrices.filter(
    (bpp) => bpp.billingProduct.type === type,
  );

export const getSubscriptionPricesByType = (
  billingSubscription: BillingSubscription,
  type: BillingProductTypeEnum,
) =>
  billingSubscription.billingProductPrices.filter(
    (bpp) => bpp.billingProduct.type === type,
  );

export const getEmployeeTierPriceInPounds = (
  employeePrice: BillingProductPrice,
  employeeCount: number,
): Pounds =>
  convertPenceToPounds(
    employeePrice.tiers.find(
      (t) =>
        employeeCount >= t.quantityStart &&
        (t.quantityEnd === null || employeeCount <= t.quantityEnd),
    ).unitPrice,
  );

// Return the level of plan change from PlanChangeLevelEnum
export const checkPlanChangeLevel = (
  currentPackage: BillingPackage,
  chosenPackage: BillingPackage,
): PlanChangeLevel => {
  const currentPackagePlan = getPackagePricesByType(
    currentPackage,
    BillingProductTypeEnum.Plan,
  )[0].billingProduct;

  const chosenPackagePlan = getPackagePricesByType(
    chosenPackage,
    BillingProductTypeEnum.Plan,
  )[0].billingProduct;

  // Certain criteria for each level
  const checkCrit = {
    downgrade: currentPackagePlan.name !== PlanNameEnum.StartupShiftie,
    upgrade: currentPackagePlan.name !== PlanNameEnum.VeryShiftie,
    likeForLike: currentPackagePlan.lookupKey === chosenPackagePlan.lookupKey,
  };

  // If it's not a likeForLike and could be a downgrade, ensure it is
  // If it's not a downgrade it must be an upgrade
  if (!checkCrit.likeForLike && checkCrit.downgrade) {
    checkCrit.downgrade =
      (currentPackagePlan.name === PlanNameEnum.VeryShiftie &&
        chosenPackagePlan.name !== PlanNameEnum.VeryShiftie) ||
      (currentPackagePlan.name === PlanNameEnum.ALittleBitShiftie &&
        chosenPackagePlan.name === PlanNameEnum.StartupShiftie);
  }

  if (checkCrit.likeForLike) return PlanChangeLevelEnum.LikeForLike;
  if (checkCrit.downgrade) return PlanChangeLevelEnum.Downgrade;
  if (checkCrit.upgrade) return PlanChangeLevelEnum.Upgrade;
  throw new Error('Could not find plan change level');
};

// Return the interval of plan change from PlanChangeIntervalEnum
export const checkPlanChangeInterval = (
  currentPackage: string,
  chosenPackage: string,
): PlanChangeInterval => {
  // Takes current and chosen plan intervals from BillingPlanIntervalEnum
  if (
    ![currentPackage, chosenPackage].every((p) =>
      Object.values<string>(BillingPackageIntervalEnum).includes(p),
    )
  ) {
    throw new Error(
      'checkPlanChangeInterval parameters should be intervals from BillingPackageIntervalEnum',
    );
  }

  if (chosenPackage === currentPackage) {
    return chosenPackage === BillingPackageIntervalEnum.Annual
      ? PlanChangeIntervalEnum.AnnuallyToAnnually
      : PlanChangeIntervalEnum.MonthlyToMonthly;
  }
  return currentPackage === BillingPackageIntervalEnum.Annual
    ? PlanChangeIntervalEnum.AnnuallyToMonthly
    : PlanChangeIntervalEnum.MonthlyToAnnually;
};

export const paymentMethodHasExpired = (
  paymentMethod: BillingPaymentMethod,
  timezone: string,
): boolean => {
  if (paymentMethod.type === 'bacs_debit') {
    return false;
  }

  const expiryDate = spacetime
    .now(timezone)
    .month(paymentMethod.cardExpMonth - 1)
    .year(
      String(paymentMethod.cardExpYear).length < 4
        ? Number(`20${paymentMethod.cardExpYear}`)
        : paymentMethod.cardExpYear,
    )
    .endOf('month')
    .toNativeDate();

  return isPast(expiryDate);
};
